/**
 * MODULE: _shared/lib/frontend/api/fetchWithAuthRetry.ts
 * PURPOSE: Browser fetch wrapper — Bearer headers; on 401, single deduped refresh and one retry
 * PUBLIC_API: fetchWithAuthRetry, CrmHttpError
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-06-04 [2.2.1273] — Refresh cooldown applies only when JWT is expired (valid tokens are not blocked).
 * RECENT_PRIOR: 2026-06-04 — Removed proactive pre-fetch refresh (parallel CRM hooks caused refresh **401** storms).
 */

import {
  isBraznRefreshCooldownActive,
  refreshBraznAccessTokenOnce,
} from './authTokenRefresh';
import { getAuthHeaders, getAuthToken, isAccessTokenExpired } from './config';

export class CrmHttpError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'CrmHttpError';
    this.status = status;
  }
}

function mergeAuthHeaders(init?: RequestInit): Headers {
  const headers = new Headers(init?.headers);
  for (const [k, v] of Object.entries(getAuthHeaders())) {
    if (!headers.has(k)) headers.set(k, v);
  }
  return headers;
}

function unauthorizedResponse(): Response {
  return new Response(JSON.stringify({ detail: 'Unauthorized' }), {
    status: 401,
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Same-origin fetch with Bearer headers.
 * - Skips network when no token or refresh cooldown is active (session cleared after refresh **401**).
 * - On response **401**, runs one deduped refresh and retries exactly once (no pre-request refresh).
 */
export async function fetchWithAuthRetry(url: string, init?: RequestInit): Promise<Response> {
  const token = getAuthToken();
  if (!token) {
    return unauthorizedResponse();
  }
  // Cooldown only applies after a failed refresh — do not block valid JWTs (session modal recovery).
  if (isBraznRefreshCooldownActive() && isAccessTokenExpired(token)) {
    return unauthorizedResponse();
  }

  const doFetch = () => fetch(url, { ...init, headers: mergeAuthHeaders(init) });

  const first = await doFetch();
  if (first.status !== 401) return first;

  const refreshed = await refreshBraznAccessTokenOnce();
  if (!refreshed || !getAuthToken()) return first;

  return doFetch();
}
